import { useQuery } from '@tanstack/react-query'
import { apiService } from '@/data/services/api/apiService'
import { CACHE_TIMES } from '@/data/services/api/cacheConfig'
import { endpoints } from '@/data/services/api/endpoints'
import { errorService } from '@/data/services/error/errorService'

/**
 * Fetch available dates that have video data for a site
 * Simple API wrapper - no complex logic
 */
export function useSiteVideoAvailableDates(siteId) {
  return useQuery({
    queryKey: ['site', siteId, 'videoAvailableDates'],
    queryFn: async () => {
      try {
        if (!siteId) {
          throw new Error('Site ID is required')
        }

        const response = await apiService.request(endpoints.sites.getVideoAvailableDates(siteId))

        if (!response.data?.success) {
          throw new Error(response.data?.error || 'Failed to fetch available dates')
        }

        // Return processed data for easy consumption
        const processedData = {
          availableDates: new Set(response.data.available_dates || []), // Set for fast lookup
          dateList: response.data.available_dates || [], // Array for iteration
          totalDays: response.data.total_dates || 0,
          dateRange: {
            earliest: response.data.available_dates?.[response.data.available_dates.length - 1], // Assuming sorted desc
            latest: response.data.available_dates?.[0],
          },
        }

        return processedData
      } catch (err) {
        errorService.logError(err, {
          context: 'useSiteVideoAvailableDates',
          siteId,
          message: `Failed to fetch available dates for site ${siteId}`,
        })
        throw err
      }
    },
    enabled: !!siteId,
    staleTime: CACHE_TIMES.SHORT_LIVED, // 5 minutes - dates don't change frequently
    gcTime: CACHE_TIMES.SHORT_CACHE, // 10 minutes
    retry: 2,
  })
}
